import React, { createContext, useContext, useEffect, useMemo, useReducer } from "react";
import { getProductImage } from "../lib/categoryImages";

const CartContext = createContext(null);

const STORAGE_KEY = "brokeshopping.cart.v1";

const initialState = { items: [] };

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return initialState;
    return parsed;
  } catch {
    return initialState;
  }
}

function reducer(state, action) {
  switch (action.type) {
    case "ADD": {
      const { product, quantity = 1 } = action;
      const existing = state.items.find((i) => i.productId === product.id);
      let items;
      if (existing) {
        items = state.items.map((i) =>
          i.productId === product.id ? { ...i, quantity: i.quantity + quantity } : i
        );
      } else {
        items = [
          ...state.items,
          {
            productId: product.id,
            title: product.title,
            price: product.price,
            image: getProductImage(product),
            category: product.category,
            categoryLabel: product.categoryLabel,
            quantity,
          },
        ];
      }
      return { ...state, items };
    }
    case "REMOVE": {
      return { ...state, items: state.items.filter((i) => i.productId !== action.productId) };
    }
    case "UPDATE_QTY": {
      const q = Math.max(1, Math.min(99, Number(action.quantity) || 1));
      return {
        ...state,
        items: state.items.map((i) =>
          i.productId === action.productId ? { ...i, quantity: q } : i
        ),
      };
    }
    case "CLEAR":
      return { ...state, items: [] };
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadInitial);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* ignore storage errors */
    }
  }, [state]);

  const value = useMemo(() => {
    const itemCount = state.items.reduce((s, i) => s + i.quantity, 0);
    const subtotal = state.items.reduce((s, i) => s + i.quantity * Number(i.price || 0), 0);
    return {
      items: state.items,
      itemCount,
      subtotal,
      addItem: (product, quantity = 1) => dispatch({ type: "ADD", product, quantity }),
      removeItem: (productId) => dispatch({ type: "REMOVE", productId }),
      updateQty: (productId, quantity) => dispatch({ type: "UPDATE_QTY", productId, quantity }),
      clear: () => dispatch({ type: "CLEAR" }),
    };
  }, [state]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
